/**
 * @file Leads service.
 * @description Handles lead upload, listing, export, and dependent advertiser/campaign lookup APIs.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { apiFetch, buildApiUrl } from '../api/api';
import { API_ENDPOINTS } from '../constants/api';
import { AdvertisersService } from './advertisersService';
import { CampaignsService } from './campaignsService';
import { LeadsApiResponse, Advertiser, Campaign, Lead, ApiResponse } from '../types';
import { isTokenExpired, createTokenExpiredError } from '../utils/tokenValidation';
import { showTokenExpiredNotification } from '../utils/notificationService';
import Papa from 'papaparse';
import type { ParseResult } from 'papaparse';
import type {
  UploadResult,
  UploadOptions,
  UploadJob,
  LatestUploadJobPayload,
} from '../types/api/leads';
import type { ServiceLead } from '../types/api/serviceApi';

export type {
  UploadResult,
  UploadOptions,
  UploadJob,
  LatestUploadJobPayload,
} from '../types/api/leads';

export interface ExportFilterPagination {
  page: number;
  per_page: number;
  total: number;
  total_page: number;
}

export interface ExportFilterLegacyMeta {
  file_name: string;
  download_url: string;
  file_size: number;
  total_records: number;
  created_at: string;
  is_existing: boolean;
  lock_id: number;
}

export interface ExportFilterResult {
  status: string;
  message: string;
  leads: ServiceLead[];
  pagination: ExportFilterPagination;
  exportUrl: string | null;
  exportMeta: ExportFilterLegacyMeta | null;
}

function exportStrField(value: unknown): string {
  return value == null ? '' : String(value);
}

function exportNumOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeExportLeadRow(row: unknown): ServiceLead {
  const r = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const lead: ServiceLead = {
    id: exportStrField(r['id'] ?? r['lead_id']),
    name: exportStrField(r['name'] ?? r['Name']),
    phone: exportStrField(r['phone'] ?? r['Phone']),
    dob: (r['dob'] as string | null) ?? null,
    age: exportNumOrNull(r['age'] ?? r['Age']),
    city: exportStrField(r['city'] ?? r['City']),
    income: exportNumOrNull(r['income'] ?? r['Income']),
    email: exportStrField(r['email'] ?? r['Email']),
    source: exportStrField(r['source'] ?? r['source_id'] ?? r['Source']),
    source_name: exportStrField(r['source_name'] ?? r['source'] ?? r['Source']),
    created_at: exportStrField(r['created_at'] ?? r['uploaded_at'] ?? r['upload_date']),
    advertiser_name: exportStrField(r['advertiser_name'] ?? r['Advertiser']),
    campaign_name: exportStrField(r['campaign_name'] ?? r['Campaign']),
    company_name: exportStrField(
      r['company_name'] ?? r['Company Name'] ?? r['Company'] ?? r['company'],
    ),
    vehicle_brand_name: exportStrField(
      r['vehicle_brand_name'] ?? r['Vehicle Brand Name'] ?? r['VehicleBrandName'],
    ),
    dnd_status: exportStrField(r['dnd_status'] ?? r['DND Status'] ?? r['DndStatus']),
    plan: exportStrField(r['plan'] ?? r['Plan']),
    user_id: exportStrField(r['user_id']),
    pincode: (r['pincode'] as string | null) ?? null,
    updated_at: (r['updated_at'] as string | null) ?? null,
    deleted_at: (r['deleted_at'] as string | null) ?? null,
  };
  if (r['data_type'] != null) lead.data_type = exportStrField(r['data_type']);
  return lead;
}

function normalizeExportFilterResponse(raw: unknown): ExportFilterResult {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const status = exportStrField(root['status'] ?? (root['success'] === true ? 'success' : ''));
  const message = exportStrField(root['message']);

  const dataBlock =
    root['data'] && typeof root['data'] === 'object' && !Array.isArray(root['data'])
      ? (root['data'] as Record<string, unknown>)
      : null;

  const pagSource =
    root['pagination'] ??
    (dataBlock ? dataBlock['pagination'] : undefined);

  const pag =
    pagSource && typeof pagSource === 'object'
      ? (pagSource as Record<string, unknown>)
      : {};

  const page = Number(pag['page'] ?? 1) || 1;
  const per_page = Number(pag['per_page'] ?? 10) || 10;
  const total = Number(pag['total'] ?? pag['total_records'] ?? 0) || 0;
  let total_page = Number(pag['total_page'] ?? pag['total_pages'] ?? 0) || 0;
  if (total_page === 0 && total > 0 && per_page > 0) {
    total_page = Math.ceil(total / per_page);
  }

  let rows: unknown[] = [];
  if (Array.isArray(root['data'])) {
    rows = root['data'];
  } else if (dataBlock) {
    if (Array.isArray(dataBlock['data'])) rows = dataBlock['data'];
    else if (Array.isArray(dataBlock['leads'])) rows = dataBlock['leads'];
  }

  const exportUrlRaw =
    root['export_url'] ??
    root['exportUrl'] ??
    root['download_url'] ??
    dataBlock?.['export_url'] ??
    dataBlock?.['exportUrl'] ??
    dataBlock?.['download_url'];
  const exportUrl = exportStrField(exportUrlRaw) || null;

  const exportObj =
    (root['export'] && typeof root['export'] === 'object'
      ? root['export']
      : dataBlock?.['export'] && typeof dataBlock['export'] === 'object'
        ? dataBlock['export']
        : null) as Record<string, unknown> | null;

  const exportMetaSource =
    exportObj ??
    (dataBlock &&
    (dataBlock['file_name'] || dataBlock['download_url'] || dataBlock['file_size'] != null)
      ? dataBlock
      : null);

  let exportMeta: ExportFilterLegacyMeta | null = null;
  if (exportMetaSource) {
    exportMeta = {
      file_name: exportStrField(exportMetaSource['file_name']),
      download_url: exportStrField(
        exportMetaSource['download_url'] ?? exportMetaSource['export_url'],
      ),
      file_size: Number(exportMetaSource['file_size'] ?? 0) || 0,
      total_records: Number(exportMetaSource['total_records'] ?? total) || total,
      created_at: exportStrField(exportMetaSource['created_at']),
      is_existing: Boolean(exportMetaSource['is_existing']),
      lock_id: Number(exportMetaSource['lock_id'] ?? 0) || 0,
    };
  }

  return {
    status: status || 'success',
    message,
    leads: rows.map(normalizeExportLeadRow),
    pagination: { page, per_page, total, total_page },
    exportUrl,
    exportMeta,
  };
}

export type {
  UploadResult,
  UploadOptions,
  UploadJob,
  LatestUploadJobPayload,
} from '../types/api/leads';
export type { UploadProgress } from '../types/api/leads';

function tryJobId(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const s = String(value).trim();
  return s.length > 0 ? s : undefined;
}

function extractUploadAsyncJobId(parsed: unknown, raw: Record<string, unknown>): string | undefined {
  const root = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  const data = root['data'];
  const dataObj = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  const job =
    root['job'] ??
    dataObj?.['job'] ??
    (dataObj && 'id' in dataObj && 'status' in dataObj ? dataObj : null);

  const jobObj = job && typeof job === 'object' ? (job as Record<string, unknown>) : null;

  const candidates: unknown[] = [
    raw['job_id'],
    raw['id'],
    raw['upload_job_id'],
    root['job_id'],
    root['id'],
    dataObj?.['job_id'],
    dataObj?.['id'],
    jobObj?.['id'],
  ];

  for (const c of candidates) {
    const id = tryJobId(c);
    if (id) return id;
  }
  return undefined;
}

function isApiEnvelopeStatus(status: unknown): boolean {
  const s = String(status ?? '').toLowerCase();
  return s === 'success' || s === 'error';
}

/** True when object looks like a job row, not the outer API envelope. */
function isUploadJobShape(obj: Record<string, unknown>): boolean {
  const hasId = tryJobId(obj['id'] ?? obj['job_id'] ?? obj['upload_job_id']) != null;
  const hasJobMetrics =
    obj['progress'] != null ||
    obj['processed_records'] != null ||
    obj['total_records'] != null ||
    obj['inserted_records'] != null ||
    obj['duplicate_records'] != null ||
    obj['job_status'] != null ||
    obj['upload_status'] != null;

  const statusRaw = obj['status'];
  const statusIsJob =
    typeof statusRaw === 'string' &&
    statusRaw.trim() !== '' &&
    !isApiEnvelopeStatus(statusRaw);

  return Boolean(hasId && (hasJobMetrics || statusIsJob));
}

/** Map one API job object (many field aliases) into UploadJob. */
function coerceUploadJob(
  candidate: unknown,
  fallbackJobId?: string,
): UploadJob | null {
  if (!candidate || typeof candidate !== 'object') return null;
  const o = candidate as Record<string, unknown>;

  const id =
    tryJobId(o['id'] ?? o['job_id'] ?? o['upload_job_id']) ?? fallbackJobId;
  if (!id) return null;

  let status = String(
    o['status'] ?? o['job_status'] ?? o['upload_status'] ?? o['state'] ?? '',
  ).trim();

  if (!status || isApiEnvelopeStatus(status)) {
    if (o['completed_at']) {
      status = 'completed';
    } else if (
      o['progress'] != null ||
      o['processed_records'] != null ||
      o['total_records'] != null
    ) {
      status = 'processing';
    } else {
      status = 'pending';
    }
  }

  return {
    id,
    status,
    progress: String(o['progress'] ?? o['percent'] ?? o['percentage'] ?? '0'),
    filename: o['filename'] != null ? String(o['filename']) : undefined,
    advertiser_id:
      o['advertiser_id'] != null ? String(o['advertiser_id']) : undefined,
    campaign_id: o['campaign_id'] != null ? String(o['campaign_id']) : undefined,
    user_id: o['user_id'] != null ? String(o['user_id']) : undefined,
    total_records:
      o['total_records'] != null ? String(o['total_records']) : undefined,
    processed_records:
      o['processed_records'] != null ? String(o['processed_records']) : undefined,
    inserted_records:
      o['inserted_records'] != null ? String(o['inserted_records']) : undefined,
    duplicate_records:
      o['duplicate_records'] != null ? String(o['duplicate_records']) : undefined,
    error_message:
      o['error_message'] != null
        ? String(o['error_message'])
        : o['error'] != null
          ? String(o['error'])
          : null,
    started_at: (o['started_at'] as string | null) ?? null,
    completed_at: (o['completed_at'] as string | null) ?? null,
    created_at: (o['created_at'] as string | null) ?? null,
    updated_at: (o['updated_at'] as string | null) ?? null,
  };
}

/** Parse job row from latest-job or status/{id} API envelopes. */
export function normalizeUploadJobFromResponse(
  raw: unknown,
  fallbackJobId?: string,
): UploadJob | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  const candidates: unknown[] = [];

  if (obj['job'] && typeof obj['job'] === 'object') {
    candidates.push(obj['job']);
  }
  if (obj['upload_job'] && typeof obj['upload_job'] === 'object') {
    candidates.push(obj['upload_job']);
  }
  if (isUploadJobShape(obj)) {
    candidates.push(obj);
  }

  const data = obj['data'];
  if (Array.isArray(data) && data.length > 0) {
    candidates.push(data[0]);
  } else if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (d['job'] && typeof d['job'] === 'object') candidates.push(d['job']);
    if (d['upload_job'] && typeof d['upload_job'] === 'object') {
      candidates.push(d['upload_job']);
    }
    if (isUploadJobShape(d)) candidates.push(d);
    const inner = d['data'];
    if (inner && typeof inner === 'object') {
      const nested = inner as Record<string, unknown>;
      if (nested['job'] && typeof nested['job'] === 'object') {
        candidates.push(nested['job']);
      }
      if (isUploadJobShape(nested)) candidates.push(nested);
    }
    if (fallbackJobId) {
      candidates.push(d);
    }
  }

  for (const c of candidates) {
    const job = coerceUploadJob(c, fallbackJobId);
    if (job) return job;
  }
  return null;
}

function extractUploadAsyncPriorLastId(parsed: unknown, raw: Record<string, unknown>): number | undefined {
  const root = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  const keys: unknown[] = [root['last_id'], root['previous_last_id']];
  const data = root['data'];
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    keys.push(d['last_id'], d['previous_last_id']);
  }
  keys.push(raw['last_id'], raw['previous_last_id']);
  for (const v of keys) {
    if (v === null || v === undefined || v === '') continue;
    const n = Number.parseInt(String(v), 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

/** Normalize API payloads that may be an array, wrapped in `data`, or nested under common keys. */
function normalizeListResponse<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    for (const key of ['data', 'advertisers', 'campaigns', 'items', 'records']) {
      const v = obj[key];
      if (Array.isArray(v)) return v as T[];
      if (key === 'data' && v && typeof v === 'object') {
        const inner = (v as Record<string, unknown>)['data'];
        if (Array.isArray(inner)) return inner as T[];
      }
    }
    const data = obj['data'];
    if (data && typeof data === 'object') {
      const nested = data as Record<string, unknown>;
      for (const key of ['advertisers', 'campaigns', 'items', 'records', 'data']) {
        const v = nested[key];
        if (Array.isArray(v)) return v as T[];
      }
    }
  }
  return [];
}

/**
 * Leads Service
 * Handles all leads-related API operations including upload, view, and export
 */
export class LeadsService {
  private static readonly ABSOLUTE_FILE_LIMIT_BYTES = 10 * 1024 * 1024 * 1024; // 10GB safety cap

  /**
   * Upload leads CSV to `leads-upload/upload-async` (multipart).
   * Matches API: advertiser_id, campaign_id, data_type, csv_file — with upload progress via XHR.
   */
  static async uploadLeads(
    file: File,
    options: UploadOptions,
  ): Promise<UploadResult> {
    const { advertiserId, campaignId, source, sourceId, dataType = 'lead', onProgress } = options;

    if (!file) {
      throw new Error('No file provided');
    }

    if (file.size > LeadsService.ABSOLUTE_FILE_LIMIT_BYTES) {
      throw new Error('File is too large. Please upload a file smaller than 10GB.');
    }

    const formData = new FormData();
    const adv = String(advertiserId ?? '').trim();
    const camp = String(campaignId ?? '').trim();
    const src = String(source ?? '').trim();
    const srcId = String(sourceId ?? '').trim();
    if (adv) formData.append('advertiser_id', adv);
    if (camp) formData.append('campaign_id', camp);
    if (srcId) formData.append('source_id', srcId);
    if (src) formData.append('source', src);
    formData.append('data_type', dataType);
    formData.append('csv_file', file, file.name);

    const url = buildApiUrl(API_ENDPOINTS.LEADS_UPLOAD);
    const token = localStorage.getItem('jiggle_token');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (event) => {
        if (!onProgress) return;
        if (event.lengthComputable && event.total > 0) {
          const percentage = Math.min(100, Math.round((event.loaded / event.total) * 100));
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage,
          });
        } else {
          onProgress({
            loaded: event.loaded,
            total: file.size,
            percentage: 0,
          });
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error or server is unreachable.'));
      };

      xhr.onload = () => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(xhr.responseText) as unknown;
        } catch {
          reject(new Error('Invalid JSON response from server'));
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          const msg =
            parsed &&
            typeof parsed === 'object' &&
            'message' in parsed &&
            typeof (parsed as { message: unknown }).message === 'string'
              ? (parsed as { message: string }).message
              : `API error: ${xhr.status}`;
          reject(new Error(msg));
          return;
        }

        if (
          parsed &&
          typeof parsed === 'object' &&
          'success' in parsed &&
          (parsed as { success?: boolean }).success === false
        ) {
          const envelope = parsed as {
            message?: string;
            errors?: string[] | null;
            status?: string;
          };
          const serverError =
            (Array.isArray(envelope.errors) && envelope.errors.length > 0
              ? envelope.errors.join(', ')
              : envelope.message) || 'Upload failed.';

          const tokenProbe: ApiResponse = {
            status: 'error',
            message: typeof envelope.message === 'string' ? envelope.message : serverError,
          };
          if (isTokenExpired(tokenProbe)) {
            const tokenError = createTokenExpiredError(serverError);
            showTokenExpiredNotification(serverError);
            setTimeout(() => {
              localStorage.removeItem('jiggle_token');
              localStorage.removeItem('jiggle_user');
              window.location.href = '/login';
            }, 2000);
            reject(tokenError);
            return;
          }

          reject(new Error(serverError));
          return;
        }

        const resultCandidate =
          parsed &&
          typeof parsed === 'object' &&
          'data' in parsed &&
          (parsed as { data?: unknown }).data !== undefined &&
          (parsed as { data?: unknown }).data !== null
            ? (parsed as { data: unknown }).data
            : parsed;

        const result =
          resultCandidate && typeof resultCandidate === 'object'
            ? (resultCandidate as UploadResult)
            : ({} as UploadResult);

        if (Array.isArray(result.errors) && result.errors.length > 0) {
          reject(new Error(result.errors.join(', ')));
          return;
        }

        const raw = result as unknown as Record<string, unknown>;

        const dupField = raw['duplicates'];
        const duplicateCount =
          typeof raw['duplicate_records_count'] === 'number'
            ? (raw['duplicate_records_count'] as number)
            : typeof dupField === 'number'
              ? dupField
              : Array.isArray(dupField)
                ? dupField.length
                : 0;

        const finalResult: UploadResult = {
          total:
            (typeof raw['total'] === 'number' ? raw['total'] : undefined) ??
            (((typeof raw['valid_records_count'] === 'number' ? raw['valid_records_count'] : undefined) ??
              (typeof raw['valid'] === 'number' ? raw['valid'] : 0) ??
              0) + duplicateCount),
          valid:
            (typeof raw['valid'] === 'number' ? raw['valid'] : 0) ||
            (typeof raw['valid_records_count'] === 'number' ? raw['valid_records_count'] : 0),
          duplicates: duplicateCount,
          errors: Array.isArray(raw['errors']) ? (raw['errors'] as string[]) : [],
          totalRecordsData: (raw['totalRecordsData'] as unknown[]) || [],
          validRecordsData:
            (raw['validRecordsData'] as unknown[]) ||
            (Array.isArray(raw['inserted']) ? (raw['inserted'] as unknown[]) : []) ||
            [],
          duplicateErrorData:
            (raw['duplicateErrorData'] as unknown[]) ||
            (Array.isArray(dupField) ? (dupField as unknown[]) : []) ||
            [],
          inserted: Array.isArray(raw['inserted']) ? (raw['inserted'] as unknown[]) : [],
          duplicate_records_count:
            (typeof raw['duplicate_records_count'] === 'number'
              ? raw['duplicate_records_count']
              : duplicateCount) as number,
          valid_records_count:
            (typeof raw['valid_records_count'] === 'number' ? raw['valid_records_count'] : 0) as number,
        };

        const priorEcho = extractUploadAsyncPriorLastId(parsed, raw);
        if (priorEcho !== undefined) {
          finalResult.upload_async_prior_last_id = priorEcho;
        }

        const jobIdEcho = extractUploadAsyncJobId(parsed, raw);
        if (jobIdEcho !== undefined) {
          finalResult.upload_async_job_id = jobIdEcho;
        }

        resolve(finalResult);
      };

      xhr.send(formData);
    });
  }

  /**
   * POST latest async upload job (`leads-upload/latest-job`) for progress + counters.
   * Sends `{}` body so `apiFetch` sets JSON Content-Type as required by the API.
   */
  static async getLatestUploadJob(): Promise<LatestUploadJobPayload> {
    const response = await apiFetch<
      | {
          success?: boolean;
          message?: string;
          errors?: string[] | null;
          data?: LatestUploadJobPayload;
        }
      | LatestUploadJobPayload
    >(API_ENDPOINTS.LEADS_UPLOAD_LATEST_JOB, 'POST', {});

    if (
      response &&
      typeof response === 'object' &&
      'success' in response &&
      (response as { success?: boolean }).success === false
    ) {
      const envelope = response as {
        message?: string;
        errors?: string[] | null;
      };
      const msg =
        (Array.isArray(envelope.errors) && envelope.errors.length > 0
          ? envelope.errors.join(', ')
          : envelope.message) || 'Failed to fetch upload job status.';
      throw new Error(msg);
    }

    let payload: LatestUploadJobPayload = {};

    if (
      response &&
      typeof response === 'object' &&
      'data' in response &&
      (response as { data?: unknown }).data &&
      typeof (response as { data?: unknown }).data === 'object'
    ) {
      payload = (response as { data: LatestUploadJobPayload }).data;
    } else if (response && typeof response === 'object') {
      payload = response as LatestUploadJobPayload;
    }

    const normalizedJob = normalizeUploadJobFromResponse(response);
    if (normalizedJob) {
      payload = { ...payload, job: normalizedJob };
    }

    return payload;
  }

  /**
   * GET upload job status (`leads-upload/status/{id}`) — use for live progress while job runs.
   */
  static async getUploadJobStatus(jobId: string | number): Promise<UploadJob> {
    const id = encodeURIComponent(String(jobId));
    const response = await apiFetch<
      | {
          success?: boolean;
          message?: string;
          errors?: string[] | null;
          data?: UploadJob | Record<string, unknown>;
        }
      | UploadJob
      | Record<string, unknown>
    >(`${API_ENDPOINTS.LEADS_UPLOAD_STATUS}/${id}`, 'GET');

    if (
      response &&
      typeof response === 'object' &&
      'success' in response &&
      (response as { success?: boolean }).success === false
    ) {
      const envelope = response as { message?: string; errors?: string[] | null };
      const msg =
        (Array.isArray(envelope.errors) && envelope.errors.length > 0
          ? envelope.errors.join(', ')
          : envelope.message) || 'Failed to fetch upload job status.';
      throw new Error(msg);
    }

    const job = normalizeUploadJobFromResponse(response, String(jobId));
    if (!job) {
      throw new Error('Job status response did not include job data.');
    }
    return job;
  }

  /**
   * Get leads with pagination and filters
   */
  static async getLeads(
    page: number = 1,
    perPage: number = 10,
    filters: Record<string, string> = {},
  ): Promise<LeadsApiResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters,
    });

    const response = await apiFetch<LeadsApiResponse>(
      `${API_ENDPOINTS.LEADS_VIEW}?${params}`,
      'GET',
    );

    return response;
  }

  /**
   * Get advertisers for select fields (GET /advertisers/list).
   */
  static async getAdvertisers(): Promise<Advertiser[]> {
    try {
      return await AdvertisersService.getAdvertisers();
    } catch {
      return [];
    }
  }

  /**
   * Get all campaigns (dropdowns). Cached to avoid duplicate calls on page load.
   */
  static async getCampaigns(): Promise<Campaign[]> {
    return LeadsService.fetchCampaignsDropdownCached();
  }

  static clearCampaignsDropdownCache(): void {
    LeadsService.campaignsDropdownCache = null;
    LeadsService.campaignsDropdownPromise = null;
  }

  private static campaignsDropdownCache: Campaign[] | null = null;
  private static campaignsDropdownPromise: Promise<Campaign[]> | null = null;

  private static fetchCampaignsDropdownCached(): Promise<Campaign[]> {
    if (LeadsService.campaignsDropdownCache) {
      return Promise.resolve(LeadsService.campaignsDropdownCache);
    }
    if (!LeadsService.campaignsDropdownPromise) {
      LeadsService.campaignsDropdownPromise = (async () => {
        const response = await apiFetch<unknown>(API_ENDPOINTS.CAMPAIGNS, 'GET');
        const list = normalizeListResponse<Campaign>(response);
        LeadsService.campaignsDropdownCache = list;
        return list;
      })().catch(() => {
        LeadsService.campaignsDropdownPromise = null;
        return [] as Campaign[];
      });
    }
    return LeadsService.campaignsDropdownPromise;
  }

  /**
   * Get campaigns for a specific advertiser (GET /campaigns/advertiser/{id}).
   */
  static async getCampaignsByAdvertiser(advertiserId: string): Promise<Campaign[]> {
    try {
      return await CampaignsService.getCampaignsByAdvertiser(advertiserId);
    } catch {
      return [];
    }
  }

  /**
   * Export leads to CSV or Excel
   */
  static async exportLeads(
    format: 'csv' | 'excel' = 'csv',
    filters: Record<string, string> = {},
  ): Promise<Blob> {
    const params = new URLSearchParams({
      format,
      ...filters,
    });

    const response = await fetch(
      `${API_ENDPOINTS.LEADS_EXPORT}?${params}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jiggle_token')}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Download exported file
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Validate CSV file before upload
   */
  static validateCsvFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    const allowedTypes = new Set([
      'text/csv',
      'application/csv',
      'text/plain',
    ]);
    const blockedTypes = new Set([
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ]);
    const lowerName = file.name.toLowerCase();
    const hasCsvExt = lowerName.endsWith('.csv');
    const isExcelExt = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');

    if (isExcelExt || blockedTypes.has(file.type)) {
      return { valid: false, error: 'Only CSV files are supported. Please export Excel as CSV before uploading.' };
    }

    if (!allowedTypes.has(file.type) && !hasCsvExt) {
      return { valid: false, error: 'Only CSV files are supported' };
    }

    if (file.size > LeadsService.ABSOLUTE_FILE_LIMIT_BYTES) {
      return { valid: false, error: 'File is too large. Please upload a file smaller than 10GB.' };
    }

    if (file.size === 0) {
      return { valid: false, error: 'File is empty' };
    }

    return { valid: true };
  }

  /**
   * Parse CSV file for preview
   */
  static parseCsvForPreview(
    file: File,
    onComplete: (data: unknown[], headers: string[]) => void,
    onError: (error: Error) => void,
  ): void {
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      preview: 5,
      complete: (result: ParseResult<Record<string, unknown>>) => {
        const headers = result.meta?.fields || 
          (result.data.length > 0 ? Object.keys(result.data[0] as Record<string, unknown>) : []);
        onComplete(result.data, headers.map(h => h.toLowerCase()));
      },
      error: (error: Error) => {
        onError(error);
      },
    });
  }

  /**
   * Get upload history
   */
  static async getUploadHistory(): Promise<unknown[]> {
    try {
      const response = await apiFetch<{ data: unknown[] }>(
        API_ENDPOINTS.HISTORY,
        'GET',
      );
      
      return response?.data || response || [];
    } catch {
      return [];
    }
  }

  /**
   * Delete uploaded leads
   */
  static async deleteLeads(leadIds: number[]): Promise<void> {
    await apiFetch(
      API_ENDPOINTS.LEADS_VIEW,
      'DELETE',
      { lead_ids: leadIds },
    );
  }

  /**
   * Update lead status
   */
  static async updateLeadStatus(leadId: number, status: string): Promise<void> {
    await apiFetch(
      `${API_ENDPOINTS.LEADS_VIEW}/${leadId}`,
      'PUT',
      { status },
    );
  }

  /**
   * Filter leads with export parameters (`POST view-leads-filter`).
   * API may return preview rows at root `data[]`, root `pagination`, and `export_url`.
   */
  static async filterLeadsForExport(filters: {
    advertiser_id: string;
    campaign_id: string;
    source?: string;
    from_date?: string;
    to_date?: string;
    data_type: string;
    export_log_days: string;
    min_age?: string;
    max_age?: string;
    min_income?: string;
    max_income?: string;
    company_name?: string;
    vehicle_brand_name?: string;
    dnd_status?: string;
  }): Promise<ExportFilterResult> {
    const formData = new FormData();
    formData.append('advertiser_id', filters.advertiser_id);
    formData.append('campaign_id', filters.campaign_id);
    formData.append('source', filters.source || '');
    formData.append('from_date', filters.from_date || '');
    formData.append('to_date', filters.to_date || '');
    formData.append('data_type', filters.data_type);
    formData.append('export_log_days', filters.export_log_days);
    if (filters.min_age) formData.append('min_age', filters.min_age);
    if (filters.max_age) formData.append('max_age', filters.max_age);
    if (filters.min_income) formData.append('min_income', filters.min_income);
    if (filters.max_income) formData.append('max_income', filters.max_income);
    if (filters.company_name) formData.append('company_name', filters.company_name);
    if (filters.vehicle_brand_name) formData.append('vehicle_brand_name', filters.vehicle_brand_name);
    if (filters.dnd_status) formData.append('dnd_status', filters.dnd_status);

    const response = await apiFetch<unknown>(
      `${API_ENDPOINTS.LEADS_VIEW}-filter`,
      'POST',
      formData,
    );

    return normalizeExportFilterResponse(response);
  }
}